(function (root, factory) {
  const api = factory();

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = api;
  }

  if (root && root.document) {
    root.MomeryPrintessTracking = api;
    api.bootstrap(root);
  }
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const EVENTS = Object.freeze({
    OPEN: 'momery:printess_editor_open',
    TAB_VIEW: 'momery:printess_editor_tab_view',
    SAVED: 'momery:printess_design_saved',
    CLOSE: 'momery:printess_editor_close',
    APPLIED: 'momery:printess_design_applied',
  });
  const CART_EVENTS = Object.freeze({
    SUCCEEDED: 'momery:printess_cart_add_succeeded',
    FAILED: 'momery:printess_cart_add_failed',
  });
  const DATA_ELEMENT_ID = 'MomeryPrintessTrackingData';
  const REGISTER_RETRY_INTERVAL_MS = 250;
  const REGISTER_TIMEOUT_MS = 10000;
  const SAVE_DEDUPE_WINDOW_MS = 1500;

  function normalizeId(value) {
    if (value === null || value === undefined) return '';
    return String(value).trim();
  }

  function readId(value) {
    if (!value) return '';
    if (typeof value.getId === 'function') return normalizeId(value.getId());
    if (value.id && value.id.value !== undefined) return normalizeId(value.id.value);
    return normalizeId(value.id ?? value.value);
  }

  function readTrackingData(documentRef) {
    const element = documentRef?.getElementById?.(DATA_ELEMENT_ID);
    if (!element) return null;

    try {
      const data = JSON.parse(element.textContent || 'null');
      return data && typeof data === 'object' ? data : null;
    } catch (error) {
      console.warn('[MOMERY][PRINTESS_TRACKING] Invalid Liquid tracking data', error);
      return null;
    }
  }

  function createSessionId(cryptoRef, now, random) {
    if (typeof cryptoRef?.randomUUID === 'function') return cryptoRef.randomUUID();

    return [
      'printess',
      now().toString(36),
      Math.floor(random() * Number.MAX_SAFE_INTEGER).toString(36),
    ].join('-');
  }

  function createTracker(options) {
    const windowRef = options.window;
    const documentRef = options.document;
    const trackingData = options.trackingData || null;
    const publish = options.publish;
    const MutationObserverRef = options.MutationObserver;
    const now = options.now || Date.now;
    const random = options.random || Math.random;
    const cryptoRef = options.crypto;
    const schedule = options.setTimeout || setTimeout;
    const cancelSchedule = options.clearTimeout || clearTimeout;

    let activeSession = null;
    let pendingApplication = null;
    let tabObserver = null;
    let scheduledTabSyncs = [];
    let destroyed = false;

    function safePublish(name, data) {
      try {
        const result = publish(name, data);
        if (result && typeof result.catch === 'function') {
          result.catch((error) => {
            console.warn(`[MOMERY][PRINTESS_TRACKING] Could not publish ${name}`, error);
          });
        }
      } catch (error) {
        console.warn(`[MOMERY][PRINTESS_TRACKING] Could not publish ${name}`, error);
      }
    }

    function getVariantInputValue() {
      const input = documentRef?.querySelector?.([
        'form[action*="/cart/add"] input[name="id"]:not([disabled])',
        'form[action*="/cart/add"] select[name="id"]',
        'form[data-type="add-to-cart-form"] input[name="id"]',
        'form[data-type="add-to-cart-form"] select[name="id"]',
      ].join(','));

      return normalizeId(input?.value);
    }

    function getQuantity() {
      const input = documentRef?.querySelector?.(
        'form[data-type="add-to-cart-form"] [name="quantity"]'
      );
      const quantity = Number(input?.value ?? 1);

      return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
    }

    function resolveVariantId(context, params) {
      return readId(params?.variant)
        || readId(context?.variant)
        || getVariantInputValue()
        || normalizeId(trackingData?.product?.initialVariantId);
    }

    function getCommerceData(context, params, quantityOverride) {
      const product = trackingData?.product || {};
      const variantId = resolveVariantId(context, params);
      const variants = Array.isArray(product.variants) ? product.variants : [];
      const variant = variants.find((candidate) => normalizeId(candidate?.id) === variantId)
        || variants.find((candidate) => normalizeId(candidate?.id) === normalizeId(product.initialVariantId))
        || null;
      const quantityNumber = Number(quantityOverride ?? getQuantity());
      const quantity = Number.isFinite(quantityNumber) && quantityNumber > 0 ? quantityNumber : 1;
      const priceNumber = Number(variant?.price);
      const price = Number.isFinite(priceNumber) ? priceNumber : 0;
      const productId = normalizeId(product.id);
      const currency = String(product.currency || '');
      const item = {
        item_id: variantId,
        item_name: String(product.name || ''),
        item_variant: String(variant?.name || ''),
        item_brand: String(product.brand || ''),
        item_category: String(product.category || ''),
        price,
        quantity,
        shopify_product_id: productId,
        shopify_variant_id: variantId,
      };

      if (variant?.sku) item.item_sku = String(variant.sku);

      return {
        product_id: productId,
        product_name: item.item_name,
        variant_id: variantId,
        variant_name: item.item_variant,
        price,
        currency,
        value: Number((price * quantity).toFixed(2)),
        quantity,
        item_brand: item.item_brand,
        item_category: item.item_category,
        items: [item],
      };
    }

    function payloadFor(session, extra) {
      return {
        printess_session_id: session.id,
        ...session.commerce,
        ...(extra || {}),
      };
    }

    function disconnectTabs() {
      tabObserver?.disconnect?.();
      tabObserver = null;
      scheduledTabSyncs.forEach((timer) => cancelSchedule(timer));
      scheduledTabSyncs = [];
    }

    function findActiveTab(rootNode) {
      if (!rootNode?.querySelector) return null;

      return rootNode.querySelector('.bc-tab-item.selected')
        || rootNode.querySelector('.bc-tab-item[aria-selected="true"]')
        || rootNode.querySelector('.bc-tab-item.active');
    }

    function readTabLabel(tab) {
      const label = tab?.querySelector?.('.label')?.textContent || '';
      return label.replace(/\s+/g, ' ').trim();
    }

    function syncActiveTab() {
      if (!activeSession?.shadowRoot) return;

      const label = readTabLabel(findActiveTab(activeSession.shadowRoot));
      if (!label || label === activeSession.lastTab) return;

      const isInitial = activeSession.lastTab === '';
      activeSession.lastTab = label;
      safePublish(EVENTS.TAB_VIEW, payloadFor(activeSession, {
        tab_name: label,
        tab_view_type: isInitial ? 'initial' : 'change',
      }));
    }

    function observeTabs(shadowRoot) {
      disconnectTabs();
      if (!shadowRoot) return;

      activeSession.shadowRoot = shadowRoot;
      syncActiveTab();

      if (typeof MutationObserverRef === 'function') {
        tabObserver = new MutationObserverRef(syncActiveTab);
        tabObserver.observe(shadowRoot, {
          subtree: true,
          childList: true,
          characterData: true,
          attributes: true,
          attributeFilter: ['class', 'aria-selected'],
        });
      }

      [0, 150, 500, 1000].forEach((delay) => {
        scheduledTabSyncs.push(schedule(syncActiveTab, delay));
      });
    }

    function observeComponentTabs(component) {
      disconnectTabs();
      if (!component) return;

      const tryAttach = () => {
        if (!activeSession || activeSession.component !== component) return;

        const shadowRoot = component.shadowRoot || null;
        if (!shadowRoot) return;
        if (activeSession.shadowRoot === shadowRoot && tabObserver) {
          syncActiveTab();
          return;
        }

        observeTabs(shadowRoot);
      };

      tryAttach();
      if (!activeSession?.shadowRoot) {
        [0, 150, 500, 1000].forEach((delay) => {
          scheduledTabSyncs.push(schedule(tryAttach, delay));
        });
      }
    }

    function emitClose(reason) {
      if (!activeSession) return;

      const session = activeSession;
      activeSession = null;
      disconnectTabs();
      safePublish(EVENTS.CLOSE, payloadFor(session, {
        close_reason: reason,
        design_saved: session.hasSaved,
        application_requested: session.applicationRequested,
      }));
    }

    function onOpenAsync(context) {
      if (destroyed) return Promise.resolve();
      if (activeSession) emitClose('reopened');

      const commerce = getCommerceData(context, null);
      const component = context?.integration?.getComponent?.() || null;
      activeSession = {
        id: createSessionId(cryptoRef, now, random),
        commerce,
        component,
        hasSaved: false,
        applicationRequested: false,
        lastSaveToken: '',
        lastSaveAt: 0,
        lastTab: '',
        shadowRoot: null,
      };

      safePublish(EVENTS.OPEN, payloadFor(activeSession));
      observeComponentTabs(component);

      return Promise.resolve();
    }

    function onSaveAsync(context, params) {
      if (!activeSession || destroyed) return Promise.resolve();

      const saveToken = normalizeId(params?.saveToken);
      const timestamp = now();
      if (
        saveToken
        && saveToken === activeSession.lastSaveToken
        && timestamp - activeSession.lastSaveAt < SAVE_DEDUPE_WINDOW_MS
      ) {
        return Promise.resolve();
      }

      activeSession.hasSaved = true;
      activeSession.lastSaveToken = saveToken;
      activeSession.lastSaveAt = timestamp;
      activeSession.commerce = getCommerceData(context, params);

      safePublish(EVENTS.SAVED, payloadFor(activeSession));
      return Promise.resolve();
    }

    function onAddToBasketAsync(context, params) {
      if (!activeSession || destroyed) return Promise.resolve();

      const commerce = getCommerceData(context, params);
      activeSession.applicationRequested = true;
      activeSession.commerce = commerce;

      pendingApplication = {
        sessionId: activeSession.id,
        saveToken: normalizeId(params?.saveToken),
        variantId: commerce.variant_id,
        commerce,
      };

      return Promise.resolve();
    }

    function onCloseAsync(context, params) {
      if (!activeSession || destroyed) return Promise.resolve();

      const closeParams = params || {};
      const isAddToBasket = closeParams.isAddToBasket === true || activeSession.applicationRequested;
      const reason = isAddToBasket
        ? 'application_pending'
        : activeSession.hasSaved
          ? 'saved_without_application'
          : 'abandoned';

      emitClose(reason);
      return Promise.resolve();
    }

    function matchesPendingApplication(detail) {
      if (!pendingApplication) return false;

      const detailToken = normalizeId(detail?.printessSaveToken);
      const detailVariant = normalizeId(detail?.variantId);
      if (pendingApplication.saveToken) {
        return !!detailToken && pendingApplication.saveToken === detailToken;
      }

      return !!pendingApplication.variantId
        && pendingApplication.variantId === detailVariant;
    }

    function onCartAddSucceeded(event) {
      if (!matchesPendingApplication(event?.detail)) return;

      const application = pendingApplication;
      pendingApplication = null;
      safePublish(EVENTS.APPLIED, {
        printess_session_id: application.sessionId,
        ...application.commerce,
      });
    }

    function onCartAddFailed(event) {
      if (!pendingApplication) return;

      const detail = event?.detail || {};
      const hasIdentity = normalizeId(detail.printessSaveToken) || normalizeId(detail.variantId);
      if (!hasIdentity || matchesPendingApplication(detail)) pendingApplication = null;
    }

    function onPageHide() {
      if (activeSession) emitClose('navigation');
      disconnectTabs();
      pendingApplication = null;
    }

    function destroy() {
      if (destroyed) return;
      onPageHide();
      destroyed = true;
      documentRef?.removeEventListener?.(CART_EVENTS.SUCCEEDED, onCartAddSucceeded);
      documentRef?.removeEventListener?.(CART_EVENTS.FAILED, onCartAddFailed);
      windowRef?.removeEventListener?.('pagehide', onPageHide);
    }

    documentRef?.addEventListener?.(CART_EVENTS.SUCCEEDED, onCartAddSucceeded);
    documentRef?.addEventListener?.(CART_EVENTS.FAILED, onCartAddFailed);
    windowRef?.addEventListener?.('pagehide', onPageHide);

    return {
      plugin: {
        onOpenAsync,
        onCloseAsync,
        onSaveAsync,
        onAddToBasketAsync,
      },
      destroy,
      onPageHide,
      syncActiveTab,
      getState() {
        return {
          activeSessionId: activeSession?.id || null,
          pendingApplicationSessionId: pendingApplication?.sessionId || null,
          observerActive: !!tabObserver,
        };
      },
    };
  }

  function bootstrap(windowRef) {
    if (windowRef.__momeryPrintessTrackingInitialized) return;
    windowRef.__momeryPrintessTrackingInitialized = true;

    const documentRef = windowRef.document;
    const tracker = createTracker({
      window: windowRef,
      document: documentRef,
      trackingData: readTrackingData(documentRef),
      MutationObserver: windowRef.MutationObserver,
      crypto: windowRef.crypto,
      setTimeout: windowRef.setTimeout.bind(windowRef),
      clearTimeout: windowRef.clearTimeout.bind(windowRef),
      publish(name, data) {
        if (typeof windowRef.Shopify?.analytics?.publish !== 'function') {
          return Promise.resolve(false);
        }

        return windowRef.Shopify.analytics.publish(name, data);
      },
    });

    windowRef.__momeryPrintessTrackingRuntime = tracker;

    let retryTimer = null;
    const startedAt = Date.now();
    const register = () => {
      if (windowRef.__momeryPrintessTrackingPluginRegistered) return true;
      if (typeof windowRef.registerPrintessPlugin !== 'function') return false;

      windowRef.registerPrintessPlugin(tracker.plugin);
      windowRef.__momeryPrintessTrackingPluginRegistered = true;
      return true;
    };

    if (!register()) {
      retryTimer = windowRef.setInterval(() => {
        if (register() || Date.now() - startedAt >= REGISTER_TIMEOUT_MS) {
          windowRef.clearInterval(retryTimer);
          retryTimer = null;
        }
      }, REGISTER_RETRY_INTERVAL_MS);
    }

    windowRef.addEventListener('pagehide', () => {
      if (retryTimer) {
        windowRef.clearInterval(retryTimer);
        retryTimer = null;
      }
    }, { once: true });
  }

  return {
    EVENTS,
    CART_EVENTS,
    SAVE_DEDUPE_WINDOW_MS,
    createSessionId,
    createTracker,
    readTrackingData,
    bootstrap,
  };
});
